import React from "react";
import * as yup from "yup";
import { Formik, Field, Form, ErrorMessage } from "formik";
import {
  Button,
  Typography,
  Container,
  Drawer,
  GlobalStyles,
} from "@mui/material";
import { CloseIcon, FilterIcon } from "@components/Icons";
import { useSnackbar } from "notistack";
import MaskedCpfCnpjField from "@components/MaskedCpfCnpjField";
import { isValidCNPJ } from "@utils/cnpj";
import { isValidCPF } from "@utils/index";

interface Props {
  drawer: boolean;
  handle: () => void;
  throttle: (next: string) => void;
  initialPage: React.Dispatch<React.SetStateAction<number>>;
}

const validationSchema = yup.object().shape({
  firstName: yup.string().matches(/^[A-Za-z\s]*$/, "Insira um nome válido"),
  email: yup.string().email("Insira um e-mail válido"),
  cnpj: yup
    .string()
    .notRequired()
    .test("cnpj", "Insira um CNPJ ou CPF válido", (value) => {
      if (!value) return true;
      const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
      return isValidCPF(cleaned) || isValidCNPJ(cleaned);
    }),
  socialName: yup.string(),
});

const fieldStyle: React.CSSProperties = {
  margin: "1rem 0",
  padding: "1rem",
  borderRadius: "4px",
  border: "1px solid #BABABA",
  fontFamily: "Montserrat",
  fontSize: "1.4rem",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
};

const FilterListUsers: React.FC<Props> = ({
  drawer,
  handle,
  throttle,
  initialPage,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = (values: {
    firstName: string;
    email: string;
    cnpj: string;
    socialName: string;
  }) => {
    if (
      !values.firstName &&
      !values.email &&
      !values.cnpj &&
      !values.socialName
    ) {
      enqueueSnackbar("Preencha pelo menos um campo", { variant: "warning" });
      return;
    }
    const cnpjCleaned = values.cnpj.replace(/[^a-zA-Z0-9]/g, "");
    throttle(
      values.email || values.firstName || cnpjCleaned || values.socialName
    );
    initialPage(0);
    handle();
  };

  return (
    <>
      <GlobalStyles
        styles={{
          ".filter-field": {
            margin: "1rem 0",
            padding: "1rem",
            borderRadius: "4px",
            border: "1px solid #BABABA",
            fontFamily: "Montserrat",
            fontSize: "1.4rem",
            width: "100%",
            boxSizing: "border-box",
            outline: "none",
          },
        }}
      />
      <Drawer anchor="right" open={drawer} onClose={handle} variant="temporary">
        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "5rem 2rem",
            width: { xs: "100vw", sm: 400 },
          }}
        >
          <Container
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h4">Filtrar dados</Typography>
            <Button onClick={handle}>
              <CloseIcon />
            </Button>
          </Container>

          <Container
            sx={{ display: "flex", justifyContent: "center", mt: "7rem" }}
          >
            <FilterIcon />
          </Container>

          <Container>
            <Formik
              initialValues={{
                firstName: "",
                email: "",
                cnpj: "",
                socialName: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              <Form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: "7rem",
                  justifyContent: "space-between",
                }}
              >
                <Field
                  style={fieldStyle}
                  name="firstName"
                  type="text"
                  placeholder="Nome"
                />
                <ErrorMessage name="firstName" />

                <Field
                  style={fieldStyle}
                  name="email"
                  type="email"
                  placeholder="E-mail"
                />
                <ErrorMessage name="email" />

                <MaskedCpfCnpjField
                  name="cnpj"
                  placeholder="CNPJ/CPF"
                  className="filter-field"
                />
                <ErrorMessage name="cnpj" />

                <Field
                  style={fieldStyle}
                  name="socialName"
                  type="text"
                  placeholder="Razão social"
                />
                <ErrorMessage name="socialName" />

                <Container
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: "8rem",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    sx={{
                      textTransform: "capitalize",
                      boxShadow: "none",
                      fontSize: "1.4rem",
                    }}
                  >
                    Filtrar
                  </Button>
                </Container>
              </Form>
            </Formik>
          </Container>
        </Container>
      </Drawer>
    </>
  );
};

export default FilterListUsers;
